// ============================================================================
// ai-model-roster.ts: what the AI Models page says about each model.
// ----------------------------------------------------------------------------
// Two halves:
//
// 1. BRAIN_ROLES and BRAIN_DROPPED describe the Quote Brain's crew. The models
//    themselves come from the backend registry through GET /api/ai-models/costs
//    (backend-ai-task-models.ts); this file holds only the words and the
//    evidence each role was chosen on. Nothing here picks a model.
//
// 2. ROSTER_GROUPS places every browser AI task (model-selector listAllTasks)
//    in one group on one tab. "Every job" holds the tasks that run whatever
//    writes the quote; "Old pipeline" holds the ones a Quote Brain job never
//    runs, and the registry entries nothing calls. Checked against the code
//    on 2026-10-29: the eight old-pipeline tasks are skipped on Quote Brain
//    jobs (App.tsx, quote-brain-project.ts), and the Risk report still runs on
//    one when a builder asks for it. ai-model-roster.test.ts fails when a
//    registered task has no group, so a new task is placed on purpose.
// ============================================================================

export type BrainRoleId = 'writer' | 'request-reader' | 'market' | 'past-quotes';

export interface BrainRoleCopy {
    id: BrainRoleId | 'checks';
    name: string;
    title: string;
    icon: string;
    /** What the role does on a job, in one or two sentences. */
    does: string;
    /** How often it runs on a job. */
    runs: string;
    /** Why this model, with the date it was measured. */
    evidence: string;
    /** What the business can do instead, since no role's model is a choice. */
    steer: string;
    /** Where that is done, when somewhere in the app. */
    steerAction?: 'pricing' | 'settings';
    /** Set when the role has no model. */
    code?: boolean;
}

export const BRAIN_ROLES: readonly BrainRoleCopy[] = Object.freeze([
    {
        id: 'writer',
        name: 'Writer',
        title: 'The writer',
        icon: 'Brain',
        does: 'Writes the whole quote. It reads the job\'s files and photos, works out the scope, prices each item from your Pricing page, then checks its own work and fixes what it finds.',
        runs: 'On every job, from start to finish.',
        evidence: 'In October 2026 QuoteMaker ran six other models on the same jobs: Kimi K3.1 Pro, GPT-6.1 Sol, Gemini 4 Argon, Muse Wonder, DeepSeek V4.1 Flash and Sonnet 5.5. None wrote quotes as well, so Opus 5.5 stays.',
        steer: 'Keep your Pricing page current and put the files and notes on each job. A price you write in a job\'s Quote Notes prints as written.',
        steerAction: 'pricing',
    },
    {
        id: 'request-reader',
        name: 'Request reader',
        title: 'The request reader',
        icon: 'ClipboardCheck',
        does: 'Before writing starts, it lists everything the request asks for and every area the site report raises. The quote cannot finish until each one is covered. It writes nothing the client sees.',
        runs: 'Once a job, before writing starts.',
        evidence: 'Added in October 2026 so no part of a request is missed.',
        steer: 'Put the full request and site report on the job. That is what it reads.',
    },
    {
        id: 'market',
        name: 'Market prices',
        title: 'Market prices',
        icon: 'Globe',
        does: 'When a product is not on your Pricing page, it looks up supplier prices on the web. It sends only product and supplier names, never client details, and every price it keeps names its source.',
        runs: 'Only when a product is not on your Pricing page.',
        evidence: 'Chosen in October 2026. On the same searches Gemini 4 Argon found prices for 22 of 32 products, against 13 for Gemini 3.8 Flash, and answered faster.',
        steer: 'Add the products you use often to your Pricing page. The writer then prices them from there and does not need to search.',
        steerAction: 'pricing',
    },
    {
        id: 'past-quotes',
        name: 'Past quotes',
        title: 'Past quotes',
        icon: 'History',
        does: 'Finds your recent quotes and your past quotes for the same client, the same site and the same kind of work, so the writer prices the job the way you have before.',
        runs: 'A few lookups a job.',
        evidence: 'The same search model QuoteMaker uses elsewhere. It finds quotes by what they mean, not only by matching words.',
        steer: 'Each quote you send is added, and a re-send replaces the earlier copy. So the next job is priced the way you priced the last.',
    },
    {
        id: 'checks',
        name: 'Pricing and checks',
        title: 'Pricing and checks',
        icon: 'Calculator',
        does: 'Plain code does the maths, not AI. Each line is priced from your Pricing page, your notes or a market price. Before a quote finishes, code checks that it covers the request and fits the job. With enough past quotes, it also compares prices and quantities with them.',
        runs: 'Every job.',
        evidence: 'Code gives the same answer every time.',
        steer: 'Your Pricing page and your notes set the prices it works from. The checks are the same for every business.',
        steerAction: 'pricing',
        code: true,
    },
]);

export interface DroppedRoleCopy {
    id: string;
    name: string;
    does: string;
    evidence: string;
}

export const BRAIN_DROPPED: readonly DroppedRoleCopy[] = Object.freeze([
    {
        id: 'readers',
        name: 'Photo and PDF readers',
        does: 'A cheaper model that described each photo and document before writing started.',
        evidence: 'Tried in October 2026 with Gemini 3.5 Flash Lite and Gemini 3.8 Flash. Neither improved quotes without making the prices worse, so the writer reads the files itself.',
    },
    {
        id: 'critic',
        name: 'Second-model critic',
        does: 'A second model that checked the draft before it finished.',
        evidence: 'Tried in October 2026. It improved quotes too little to be worth the extra time.',
    },
]);

// ── Browser tasks ──────────────────────────────────────────────────────────

export type RosterTab = 'every' | 'old';

export interface RosterGroup {
    id: string;
    tab: RosterTab;
    name: string;
    icon: string;
    blurb: string;
    tasks: readonly string[];
    /** Not part of a job, so costs read as a total, not per job. */
    notAJob?: boolean;
    /** Registry entries nothing calls: shown, never offered as a choice. */
    inactive?: boolean;
}

export const ROSTER_GROUPS: readonly RosterGroup[] = Object.freeze([
    {
        id: 'reading',
        tab: 'every',
        name: 'Reading the files',
        icon: 'FileText',
        blurb: 'Reads each file as it arrives: the text in photos and PDFs, what each document is, the client, plans and the request. Also tidies site visit reports.',
        tasks: [
            'image-text-triage', 'image-ocr', 'pdf-ocr', 'doc-classify', 'classification-verifier',
            'photo-label', 'photo-description', 'plan-analysis', 'contact-extraction', 'rfq-summary',
            'assessor-extraction', 'quote-import-extraction', 'requote-scope-extraction',
            'intake-email-extract', 'intake-sdp-qc-assessment', 'site-visit-report-tidy',
        ],
    },
    {
        id: 'media',
        tab: 'every',
        name: 'Site video and audio',
        icon: 'Video',
        blurb: 'Turns site videos and voice notes into text and finds the key moments.',
        tasks: ['audio-transcription', 'video-processing', 'key-moments'],
    },
    {
        id: 'summary',
        tab: 'every',
        name: 'The summary',
        icon: 'Sparkles',
        blurb: 'Writes the job summary and reads it aloud.',
        tasks: ['project-summary', 'summary-tts'],
    },
    {
        id: 'assistant',
        tab: 'every',
        name: 'QM Assistant',
        icon: 'MessageSquare',
        blurb: 'Everyday chat, Think harder, a close look at a site photo you ask about, and shortening a long chat.',
        tasks: ['ai-assistant', 'ai-assistant-deep', 'assistant-compaction', 'photo-forensic', 'photo-forensic-fallback'],
    },
    {
        id: 'after',
        tab: 'every',
        name: 'After the quote',
        icon: 'Send',
        blurb: 'Pasted quote text, the email that goes with the quote, work orders and the final PDF check.',
        tasks: ['quote-paste-structure', 'email-generation', 'work-order-draft', 'pdf-comparison'],
    },
    {
        id: 'tools',
        tab: 'every',
        name: 'Tools',
        icon: 'Award',
        blurb: 'Not part of a job: reading a finished quote PDF back into Quick Edit, and Review with AI.',
        tasks: ['gold-standard-extraction', 'model-advisor'],
        notAJob: true,
    },
    {
        id: 'own-quote',
        tab: 'every',
        name: 'A quote you write yourself',
        icon: 'Layers',
        blurb: 'Only on a job you write yourself: reprices an item you change at Stage 4, places the photos on the items, and checks the quote covers the request before it goes out.',
        tasks: ['cost-recompute', 'photo-auto-assign', 'photo-auto-assign-fallback', 'rfq-fit-verify'],
    },
    {
        id: 'unused',
        tab: 'old',
        name: 'Not in use',
        icon: 'Circle',
        blurb: 'No part of QuoteMaker calls these now. They stay listed so a saved choice still shows.',
        tasks: [
            'notes-scoring', 'section-extract', 'rfq-analysis', 'ai-assistant-light', 'market-price-research',
            'locality-inference', 'inclusions-generator', 'title-suggester',
            // The old pipeline's tasks (the Risk report, the Work Method and the old quote writer), retired with it.
            'risk-analysis', 'report-synthesis', 'project-strategy', 'project-strategy-fallback',
            'project-strategy-recovery', 'project-strategy-auto', 'rfq-mode-classify', 'evidence-canonicalisation',
            'diagnosis-pass-a', 'diagnosis-pass-b', 'diagnosis-pass-c', 'diagnosis-reconciliation', 'inspector-feedback',
            'wms-generation', 'work-method-statement', 'scope-morph', 'rationale-precompute', 'quote-generation',
            // The learning loop's tasks (harvest, triage, change notes, gold standards, paired and batch training), retired with it (D4).
            'learning-harvest', 'learning-triage', 'quote-change-narrative', 'semantic-tag',
            'paired-transformation-analysis', 'category-profile-redistillation',
            'batch-trainer-extract', 'batch-trainer-tag-facets', 'batch-trainer-mine-insights',
        ],
        inactive: true,
    },
]);

// What the page calls each task a builder can change, in plain words. The
// registry's own labels and descriptions (model-selector TASK_METADATA) are
// engineering notes with stage numbers, model ids and test results; the page
// shows these instead. A task with no entry here keeps the registry's words.
export const TASK_COPY: Readonly<Record<string, { label: string; description: string }>> = Object.freeze({
    'image-text-triage': { label: 'Sort images', description: 'Sorts imported images into documents, photos with writing on them, and plain photos.' },
    'image-ocr': { label: 'Read text in photos', description: 'Reads the text in photos of documents.' },
    'pdf-ocr': { label: 'Read scanned PDFs', description: 'Reads the text in scanned PDFs.' },
    'doc-classify': { label: 'Sort documents', description: 'Works out what each file is: a quote request, notes or a report.' },
    'classification-verifier': { label: 'Check the client', description: 'Double-checks the client found in the documents, so a sender is not mistaken for the client.' },
    'photo-label': { label: 'Label photos', description: 'Tags each site photo with what it shows.' },
    'photo-description': { label: 'Describe photos', description: 'Writes a short description of each photo for the rest of the job to use.' },
    'plan-analysis': { label: 'Read plans', description: 'Reads plan drawings for the layout, rooms and notes.' },
    'contact-extraction': { label: 'Find the client', description: 'Finds the client, address, strata plan and order number in the request.' },
    'rfq-summary': { label: 'Sum up the request', description: 'Sums up what the quote request asks for.' },
    'assessor-extraction': { label: 'Find the inspector', description: 'Finds the inspector\'s name and visit date in the notes.' },
    'quote-import-extraction': { label: 'Import a quote', description: 'Reads an existing quote, from any builder, into items and totals so you can import it as a job.' },
    'requote-scope-extraction': { label: 'Read an earlier quote', description: 'Reads the scope from an earlier or competitor quote so it can be priced again.' },
    'intake-email-extract': { label: 'Read a request email', description: 'Reads a pasted or dropped email into the job\'s details: who asked, the property and the work.' },
    'intake-sdp-qc-assessment': { label: 'Check the site evidence', description: 'Checks the site photos and notes against the request and says whether they are enough to quote.' },
    'site-visit-report-tidy': { label: 'Tidy a site visit report', description: 'Tidies a spoken site visit report into sections, without adding or dropping facts.' },
    'audio-transcription': { label: 'Transcribe audio', description: 'Turns voice notes and the sound from site videos into text.' },
    'video-processing': { label: 'Prepare videos', description: 'Prepares site videos so their sound can be transcribed.' },
    'key-moments': { label: 'Find key moments', description: 'Finds the moments in a video worth keeping as a still photo.' },
    'project-summary': { label: 'Write the summary', description: 'Writes the job summary from the request, photos and notes.' },
    'summary-tts': { label: 'Read the summary aloud', description: 'Reads the job summary aloud. Pick the voice under Settings, Voice.' },
    'ai-assistant': { label: 'Assistant chat', description: 'The everyday QM Assistant.' },
    'ai-assistant-deep': { label: 'Assistant, think harder', description: 'A stronger, slower model, used only when you ask the Assistant to "think harder".' },
    'assistant-compaction': { label: 'Shorten long chats', description: 'Sums up the older part of a long Assistant chat so it can keep going.' },
    'photo-forensic': { label: 'Study a photo', description: 'Looks closely at a site photo you ask the Assistant about and explains what has failed and why.' },
    'photo-forensic-fallback': { label: 'Study a photo (backup)', description: 'A second model, used once when the first photo check fails.' },
    'quote-paste-structure': { label: 'Sort pasted quote text', description: 'When you paste a quote at Stage 5, sorts each line into titles, items and notes. It keeps your words.' },
    'email-generation': { label: 'Write the quote email', description: 'Writes the subject and message that go with the quote.' },
    'work-order-draft': { label: 'Draft the work order', description: 'Drafts the work order, by phase and trade, when a quote is won.' },
    'pdf-comparison': { label: 'Compare the sent PDF', description: 'Compares the quote with the final PDF you sent.' },
    'gold-standard-extraction': { label: 'Read a finished quote', description: 'Reads a finished quote PDF back in for Quick Edit.' },
    'model-advisor': { label: 'Review with AI', description: 'Checks the models on this page and suggests changes. Run it under Settings, Review with AI.' },
    'cost-recompute': { label: 'Reprice an item', description: 'Reprices an item when you change what it includes at Stage 4.' },
    'photo-auto-assign': { label: 'Place photos on items', description: 'Suggests which photos go with each item.' },
    'photo-auto-assign-fallback': { label: 'Place photos (backup)', description: 'A second model, used once when the first try fails.' },
    'rfq-fit-verify': { label: 'Check the quote', description: 'Checks the quote covers the request before it goes out. It never changes prices.' },
});

const GROUP_BY_TASK: ReadonlyMap<string, RosterGroup> = new Map(
    ROSTER_GROUPS.flatMap((g) => g.tasks.map((t) => [t, g] as const)),
);

export function groupForTask(task: string): RosterGroup | null {
    return GROUP_BY_TASK.get(task) || null;
}

export function groupsForTab(tab: RosterTab): RosterGroup[] {
    return ROSTER_GROUPS.filter((g) => g.tab === tab);
}

export interface TaskSpend {
    usd: number | null;
    calls: number;
    projects: number;
}

/**
 * What a task adds to an average job: its spend in the window divided by the
 * jobs that made any browser AI call then. Dividing by only the jobs that ran
 * the task overstated a task that runs on a few jobs (a fallback, a recovery).
 * Null with no job to divide by.
 */
export function perJobUsd(spend: TaskSpend | undefined | null, jobs: number | null | undefined): number | null {
    if (!spend || !Number.isFinite(spend.usd) || !jobs) return null;
    return (spend.usd as number) / jobs;
}

export interface GroupSpend {
    totalUsd: number;
    calls: number;
    /** The group's spend over the window's jobs: what it adds to an average job. */
    perJobUsd: number | null;
}

export function groupSpend(group: RosterGroup, byTask: Record<string, TaskSpend> | null | undefined, jobs: number | null | undefined): GroupSpend {
    let totalUsd = 0;
    let calls = 0;
    let any = false;
    for (const task of group.tasks) {
        const spend = byTask?.[task];
        if (!spend) continue;
        any = true;
        totalUsd += Number(spend.usd) || 0;
        calls += spend.calls || 0;
    }
    return { totalUsd, calls, perJobUsd: any && jobs ? totalUsd / jobs : null };
}

/**
 * A tuning note as a business reads it: every sentence that quotes a price
 * dropped (Peter, 2026-10-30, "Hide from customers"). Four of the registry's
 * protected-route notes measure cost per call; the rest of each note still
 * says why the route is tuned. Null when nothing is left.
 */
export function withoutPrices(text: string | null | undefined): string | null {
    if (!text) return null;
    const kept = String(text).split(/(?<=[.!?])\s+/).filter((sentence) => !/\$\s?\d/.test(sentence));
    const out = kept.join(' ').trim();
    return out || null;
}

/** Dollars for a per-job figure: cents under a dollar keep two places, tiny sums say so. */
export function formatUsd(usd: number | null | undefined, { tiny = 0.005 } = {}): string {
    if (usd === null || usd === undefined || !Number.isFinite(usd)) return '-';
    if (usd > 0 && usd < tiny) return 'under 1c';
    return `$${usd.toFixed(2)}`;
}
